/**
 * 정사·IR 박스/폴리곤에서 L_map / N_px 추출.
 */
import { coordinateUtils } from '@/utils/polygonToImageCalculator';

/**
 * lon/lat ring [[lon,lat], ...] → 변 중 최장(m). 닫힌 링(첫=끝)도 허용.
 * @param {Array<[number, number]>} ring
 * @returns {number|null}
 */
export function longestSideMetersFromLonLatRing(ring) {
  if (!Array.isArray(ring) || ring.length < 2) {
    return null;
  }
  const first = ring[0];
  const last = ring[ring.length - 1];
  const closed =
    first &&
    last &&
    Number(first[0]) === Number(last[0]) &&
    Number(first[1]) === Number(last[1]);
  const open = closed ? ring.slice(0, -1) : ring.slice();
  if (open.length < 2) {
    return null;
  }
  let longest = 0;
  for (let i = 0; i < open.length; i++) {
    const a = open[i];
    const b = open[(i + 1) % open.length];
    if (
      !a ||
      !b ||
      !Number.isFinite(Number(a[0])) ||
      !Number.isFinite(Number(a[1])) ||
      !Number.isFinite(Number(b[0])) ||
      !Number.isFinite(Number(b[1]))
    ) {
      continue;
    }
    const d = coordinateUtils.calculateDistance(a, b);
    if (Number.isFinite(d) && d > longest) {
      longest = d;
    }
  }
  return longest > 0 ? longest : null;
}

/**
 * lon/lat extent [minLon, minLat, maxLon, maxLat] → 긴 변(m).
 * @param {number[]} extent
 * @returns {number|null}
 */
export function longestSideMetersFromLonLatExtent(extent) {
  if (!Array.isArray(extent) || extent.length < 4) {
    return null;
  }
  const [minLon, minLat, maxLon, maxLat] = extent.map(Number);
  if (
    ![minLon, minLat, maxLon, maxLat].every(Number.isFinite) ||
    maxLon <= minLon ||
    maxLat <= minLat
  ) {
    return null;
  }
  return longestSideMetersFromLonLatRing([
    [minLon, minLat],
    [maxLon, minLat],
    [maxLon, maxLat],
    [minLon, maxLat]
  ]);
}

/**
 * @param {number} width
 * @param {number} height
 * @returns {number|null}
 */
export function longestSidePx(width, height) {
  const w = Number(width);
  const h = Number(height);
  if (!Number.isFinite(w) || !Number.isFinite(h) || w <= 0 || h <= 0) {
    return null;
  }
  return Math.max(w, h);
}

/**
 * 픽셀 링 [[x,y], ...] → 변 중 최장(px). 닫힌 링 허용.
 * @param {Array<[number, number]>} ring
 * @returns {number|null}
 */
export function longestSidePxFromRing(ring) {
  if (!Array.isArray(ring) || ring.length < 2) {
    return null;
  }
  const first = ring[0];
  const last = ring[ring.length - 1];
  const closed =
    first &&
    last &&
    Number(first[0]) === Number(last[0]) &&
    Number(first[1]) === Number(last[1]);
  const open = closed ? ring.slice(0, -1) : ring.slice();
  if (open.length < 2) {
    return null;
  }
  let longest = 0;
  for (let i = 0; i < open.length; i++) {
    const a = open[i];
    const b = open[(i + 1) % open.length];
    if (
      !a ||
      !b ||
      !Number.isFinite(Number(a[0])) ||
      !Number.isFinite(Number(a[1])) ||
      !Number.isFinite(Number(b[0])) ||
      !Number.isFinite(Number(b[1]))
    ) {
      continue;
    }
    const dx = Number(b[0]) - Number(a[0]);
    const dy = Number(b[1]) - Number(a[1]);
    const d = Math.hypot(dx, dy);
    if (Number.isFinite(d) && d > longest) {
      longest = d;
    }
  }
  return longest > 0 ? longest : null;
}
